import { Params } from '@angular/router';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as config } from '../../../../environments/environment';
import { lastValueFrom, map, Observable } from 'rxjs';
import {
  AbmJourneysTotals,
  AceCampaignPerformance,
  AgregatedContentData,
  CampaignsAttributes,
  DashboardGoalReachesCount,
  DashboardGoalStats,
  IAiChatConversation,
  InsightsAreasUnitsPerformance,
  InsightsContentCompareGraph,
  InsightsContentTableItem,
  InsightsCtaPerformance,
  InsightsEventGoalsTableItem,
  InsightsEventGoalsTotals,
  InsightsJourneysTableItem,
  InsightsJourneysTotals,
  InsightsTotals,
  JourneysDetails,
  OpportunitiesAudienceStats,
  OtwPerformance,
  PagesCount,
  SourcesResponseItemCampaignType,
  SourcesResponseItemSourceType,
  SourcesTotalsResponse,
  TrdUpliftStats,
  TrenDemonAcceleratedStats,
  VisitorsJourneysTotals
} from '../models/models';
import { DataResponse, RecordsResponse } from '../../shared/models/models';
import {
  AudienceCompany,
  AudienceContentReaders,
  AudienceConversionRate,
  AudienceOpportunity,
  AudienceSize,
  AudiencesListStatsItem,
  AudienceSource
} from '../../shared/models/audiences.models';
import { RecentPageViews } from '../../shared/models/recent-page-views.models';

@Injectable({
  providedIn: 'root'
})
export class InsightsService {
  private http = inject(HttpClient);

  getDashboardGoalsStats(startDate: string, endDate: string, goalsIds: string[] | number[]): Observable<{ data: DashboardGoalStats[] }> {
    return this.http.get<{ data: DashboardGoalStats[] }>(
      `${config.insightsBaseUrl}/api/dashboard`,
      {
        params: {
          from_date: startDate,
          to_date: endDate,
          goal_id: goalsIds.join(',')
        }
      }
    );
  }

  getRevenueAudienceStats(audienceId: number, daysPeriod: 7 | 30 | 90): Observable<OpportunitiesAudienceStats> {
    return this.http.get<OpportunitiesAudienceStats>(
      `${config.insightsBaseUrl}/api/audiences/${audienceId}/revenue`, {
      params: {
        daysPeriod: daysPeriod.toString()
      }
    });
  }

  getTrenDemonAcceleratedStats(filters: Params): Observable<{ data: TrenDemonAcceleratedStats }> {
    return this.http.get<{ data: TrenDemonAcceleratedStats }>(`${config.insightsBaseUrl}/api/dashboard/accelerated`, { params: filters });
  }

  getTrdUpliftStats(filters: Params): Observable<{ data: TrdUpliftStats }> {
    return this.http.get<{ data: TrdUpliftStats }>(`${config.insightsBaseUrl}/api/dashboard/trd_uplift`, { params: filters });
  }

  getDashboardGoalReachesCount(startDate: string, endDate: string, goalId: number): Observable<{ data: DashboardGoalReachesCount[] }> {
    return this.http.get<{ data: DashboardGoalReachesCount[] }>(
      `${config.insightsBaseUrl}/api/dashboard/goal_graph`,
      {
        params: {
          from_date: startDate,
          to_date: endDate,
          goal_id: goalId.toString()
        }
      }
    );
  }

  getSourcesList(filters: Params): Observable<RecordsResponse<any>> {
    return this.http.get<RecordsResponse<SourcesResponseItemSourceType | SourcesResponseItemCampaignType>>(
      `${config.insightsBaseUrl}/api/sources/list`, {
      params: filters
    });
  }

  getSourcesDashboardTotals(filters: Params): Observable<{ data: SourcesTotalsResponse }> {
    return this.http.get<{ data: SourcesTotalsResponse }>(
      `${config.insightsBaseUrl}/api/sources/v2/dashboard_totals`, {
      params: filters
    });
  }

  getSourcesDashboardExportedUrl(filters: Params): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      `${config.insightsBaseUrl}/api/sources/v2/dashboard_export`, {
      params: filters
    });
  }
  getInsightsDashboardContentData(filters: Params): Observable<{ data: InsightsContentTableItem[] }> {
    if (filters.object_type === 'company') {
      return this.http.get<{ data: InsightsContentTableItem[] }>(
        `${config.insightsBaseUrl}/api/content/abm/pages`, { params: filters });
    } else {
      return this.http.get<{ data: InsightsContentTableItem[] }>(
        `${config.insightsBaseUrl}/api/content/dashboard`, { params: filters });
    }
  }
  getInsightsDashboardContentTotals(filters: Params): Observable<DataResponse<InsightsTotals>> {
    return this.http.get<DataResponse<InsightsTotals>>(
      `${config.insightsBaseUrl}/api/content/dashboard_stats`, {
      params: filters
    });
  }
  getInsightsContentAgregatedData(filters: Params): Observable<DataResponse<AgregatedContentData>> {
    if (filters.object_type === 'company') {
      return this.http.get<DataResponse<AgregatedContentData>>(
        `${config.insightsBaseUrl}/api/content/abm/stats`, {
        params: filters
      });
    } else {
      if (filters.insights_type === 'deals') {
        return this.http.get<DataResponse<AgregatedContentData>>(
          `${config.insightsBaseUrl}/api/content/content_deals_stats`, {
          params: filters
        });
      } else {
        return this.http.get<DataResponse<AgregatedContentData>>(
          `${config.insightsBaseUrl}/api/content/content_stats`, {
          params: filters
        });
      }
    }
  }

  getInsightsContentPagesCount(filters: Params): Observable<DataResponse<PagesCount>> {
    if (filters.object_type === 'company') {
      return this.http.get<DataResponse<PagesCount>>(
        `${config.insightsBaseUrl}/api/content/abm/pages_count`, {
        params: filters
      });
    } else {
      return this.http.get<DataResponse<PagesCount>>(
        `${config.insightsBaseUrl}/api/content/pages_count`, {
        params: filters
      });
    }
  }

  getInsightsContentGraph(filters: Params): Observable<{ data: InsightsContentCompareGraph[] }> {
    if (filters.object_type === 'company') {
      return this.http.get<{ data: InsightsContentCompareGraph[] }>(
        `${config.insightsBaseUrl}/api/content/abm/content_graph`, {
        params: filters
      });
    } else {
      if (filters.insights_type === 'deals') {
        return this.http.get<{ data: InsightsContentCompareGraph[] }>(
          `${config.insightsBaseUrl}/api/content/content_deals_graph`, {
          params: filters
        });
      } else {
        return this.http.get<{ data: InsightsContentCompareGraph[] }>(
          `${config.insightsBaseUrl}/api/content/content_graph`, {
          params: filters
        });
      }
    }
  }

  getInsightsJourneysTotals(filters: Params): Observable<InsightsJourneysTotals> {
    return this.http.get<InsightsJourneysTotals>(`${config.insightsBaseUrl}/api/journeys/totals_no_prev`, { params: filters });
  }

  getInsightsJourneysDetails(queryparams: Params): Observable<JourneysDetails> {
    return this.http.get<JourneysDetails>(`${config.insightsBaseUrl}/api/journeys/details2`, { params: queryparams });
  }

  getJourneyVisitorChats(params: Params): Observable<IAiChatConversation[]> {
    return this.http.get<any>(`${config.insightsBaseUrl}/api/journeys/visitor/ai_chats`, { params });
  }

  getInsightsJourneysData(filters: Params): Observable<InsightsJourneysTableItem[]> {
    return this.http.get<InsightsJourneysTableItem[]>(`${config.insightsBaseUrl}/api/journeys`, { params: filters });
  }

  getAbmJourneysTotals(filters: Params): Observable<AbmJourneysTotals> {
    return this.http.get<AbmJourneysTotals>(`${config.insightsBaseUrl}/api/journeys/abm_totals`, { params: filters });
  }

  getAbmJourneysData(filters: Params): Observable<InsightsJourneysTableItem[]> {
    return this.http.get<InsightsJourneysTableItem[]>(`${config.insightsBaseUrl}/api/journeys/abm`, { params: filters });
  }

  getConnectedJourneysTotals(filters: Params): Observable<VisitorsJourneysTotals> {
    return this.http.get<VisitorsJourneysTotals>(`${config.insightsBaseUrl}/api/journeys/connected_totals`, { params: filters });
  }

  getConnectedJourneysData(filters: Params): Observable<InsightsJourneysTableItem[]> {
    return this.http.get<InsightsJourneysTableItem[]>(`${config.insightsBaseUrl}/api/journeys/connected`, { params: filters });
  }

  getAiChatJourneysTotals(filters: Params): Observable<VisitorsJourneysTotals> {
    return this.http.get<VisitorsJourneysTotals>(`${config.insightsBaseUrl}/api/journeys/ai-chat-totals`, { params: filters });
  }

  getAiChatJourneysData(filters: Params): Observable<InsightsJourneysTableItem[]> {
    return this.http.get<InsightsJourneysTableItem[]>(`${config.insightsBaseUrl}/api/journeys/ai-chat`, { params: filters });
  }

  getAccountCampaignsAttibutes(): Observable<CampaignsAttributes> {
    return this.http.get<CampaignsAttributes>(
      `${config.insightsBaseUrl}/api/account/campaigns-attributes`);
  }

  getAccountRecentPageViews(): Observable<RecentPageViews[]> {
    return this.http.get<RecentPageViews[]>(
      `${config.insightsBaseUrl}/api/account/account-page-views`);
  }

  getAccountTrackedAccountsCount(): Observable<number> {
    return this.http.get<number>(
      `${config.insightsBaseUrl}/api/account/tracked-accounts-count`);
  }

  getAudienceSize(filters: Params): Observable<AudienceSize[]> {
    return this.http.get<AudienceSize[]>(
      `${config.insightsBaseUrl}/api/audiences/${filters.id}/sizes`, {
      params: filters
    });
  }

  getAudienceTotals(filters: Params): Observable<AudienceSize> {
    return this.http.get<AudienceSize>(
      `${config.insightsBaseUrl}/api/audiences/${filters.id}`, {
      params: filters
    });
  }

  getAudienceConversionRate(filters: Params): Observable<AudienceConversionRate[]> {
    return this.http.get<AudienceConversionRate[]>(
      `${config.insightsBaseUrl}/api/audiences/${filters.id}/conversions`, {
      params: filters
    });
  }

  getAudienceOpportunities(filters: Params): Observable<RecordsResponse<AudienceOpportunity>> {
    return this.http.get<RecordsResponse<AudienceOpportunity>>(
      `${config.insightsBaseUrl}/api/audiences/${filters.id}/opportunities`, {
      params: filters
    });
  }

  getAudienceMostReadContent(filters: Params): Observable<AudienceContentReaders[]> {
    return this.http.get<AudienceContentReaders[]>(
      `${config.insightsBaseUrl}/api/audiences/${filters.id}/reads`, {
      params: filters
    });
  }

  getAudienceSources(filters: Params): Observable<RecordsResponse<AudienceSource>> {
    return this.http.get<RecordsResponse<AudienceSource>>(
      `${config.insightsBaseUrl}/api/audiences/${filters.id}/sources`, {
      params: filters
    });
  }

  getAudiencesListStats(filters: Params): Observable<AudiencesListStatsItem[]> {
    return this.http.get<AudiencesListStatsItem[]>(
      `${config.insightsBaseUrl}/api/audiences/list`, {
      params: filters
    });
  }

  getInsigthsPersonalizationUnits(filters: Params): Observable<{ data: InsightsCtaPerformance[] }> {
    if (filters.insights_type === 'goals') {
      return this.http.get<{ data: InsightsCtaPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/units`, { params: filters });
    } else {
      return this.http.get<{ data: InsightsCtaPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/units/by-deals`, { params: filters });
    }
  }

  getInsigthsContexTilesUnits(filters: Params): Promise<{ data: InsightsCtaPerformance[] }> {
    if (filters.insights_type === 'goals') {
      return lastValueFrom(this.http.get<{ data: InsightsCtaPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/contextiles`, { params: filters }));
    } else {
      return lastValueFrom(this.http.get<{ data: InsightsCtaPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/contextiles/by-deals`, { params: filters }));
    }
  }

  getInsigthsContexTilesQas(filters: Params): Promise<{ data: InsightsCtaPerformance[] }> {
    if (filters.insights_type === 'goals') {
      return lastValueFrom(this.http.get<{ data: InsightsCtaPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/contextiles/qas`, { params: filters }));
    } else {
      return lastValueFrom(this.http.get<{ data: InsightsCtaPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/contextiles/qas/by-deals`, { params: filters }));
    }
  }

  getInsigthsContexTilesQasTotals(filters: Params): Promise<{ data: { total: number } }> {
    if (filters.insights_type === 'goals') {
      return lastValueFrom(this.http.get<{ data: { total: number } }>(`${config.insightsBaseUrl}/api/personalization/contextiles/qas/totals`, { params: filters }));
    } else {
      return lastValueFrom(this.http.get<{ data: { total: number } }>(`${config.insightsBaseUrl}/api/personalization/contextiles/qas/by-deals/totals`, { params: filters }));
    }
  }

  getInsigthsPersonalizationUnitsSpecificArea(filters: Params): Observable<{ data: InsightsCtaPerformance[] }> {
    return this.http.get<{ data: InsightsCtaPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/units-by-area`, { params: filters });
  }

  getInsigthsPersonalizationByAreas(filters: Params): Observable<{ data: InsightsAreasUnitsPerformance[] }> {
    return this.http.get<{ data: InsightsAreasUnitsPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/units/overview`, { params: filters });
  }
  getInsightsEventsGoalsTotals(filters: Params): Observable<{ data: InsightsEventGoalsTotals }> {
    return this.http.get<{ data: InsightsEventGoalsTotals }>(
      `${config.insightsBaseUrl}/api/event/totals`, {
      params: filters
    });
  }
  getInsightsEventsGoals(filters: Params): Observable<{ data: InsightsEventGoalsTableItem[] }> {
    return this.http.get<{ data: InsightsEventGoalsTableItem[] }>(
      `${config.insightsBaseUrl}/api/event/goals`, { params: filters });
  }

  getSalesForceOpportunityStages(): Observable<string[]> {
    return this.http.get<string[]>(
      `${config.insightsBaseUrl}/api/account/sf-opportunities-stages`);
  }

  getAudienceCompanies(filters: Params): Observable<RecordsResponse<AudienceCompany>> {
    return this.http.get<RecordsResponse<AudienceCompany>>(
      `${config.insightsBaseUrl}/api/audiences/${filters.id}/companies`, {
      params: filters
    });
  }

  getAceCampaignsInsigths(filters: Params): Promise<{ data: AceCampaignPerformance[] }> {
    return lastValueFrom(this.http.get<{ data: AceCampaignPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/ace-campaigns`, { params: filters }));
  }

  getAceVariantsInsigths(filters: Params): Promise<{ data: AceCampaignPerformance[] }> {
    return lastValueFrom(this.http.get<{ data: AceCampaignPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/ace-variants`, { params: filters }));
  }

  getOtwInsigths(filters: Params): Promise<{ data: OtwPerformance[] }> {
    return lastValueFrom(this.http.get<{ data: OtwPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/otw-campaigns`, { params: filters }));
  }

  getStreamContentInsigths(filters: Params): Promise<{ data: OtwPerformance[] }> {
    return lastValueFrom(this.http.get<{ data: OtwPerformance[] }>(`${config.insightsBaseUrl}/api/personalization/stream-content`, { params: filters }));
  }

  getExternalToken(): Promise<string> {
    return lastValueFrom(
      this.http.get<{ token: string }>(`${config.insightsBaseUrl}/api/account/external-token`)
        .pipe(map(v => v.token))
    );
  }
}
